import { CoreFetch } from '../../api/index';
import { ClaimApi } from '../../api/server/apis/ClaimApi';
import { PaymentApi } from '../../api/server/apis/PaymentApi';
import { GetAdjustmentRemarkReportRequest, ReportApi } from '../../api/server/apis/ReportApi';
import { ClaimTransmissionStatusType } from '../../api/server/models/ClaimTransmissionStatusType';
import { ClaimWorkflowHistoryItem } from '../../api/server/models/ClaimWorkflowHistoryItem';
import { ProviderAdjustmentRemarkReportItem } from '../../api/server/models/ProviderAdjustmentRemarkReportItem';
import { ProviderClaimPayment } from '../../api/server/models/ProviderClaimPayment';
import { ProviderTransmissionStatusReportItem } from '../../api/server/models/ProviderTransmissionStatusReportItem';
import { DenialsClaim, IDenialsClaimDetails } from './denial-claim';

/** How many payment ROWS one drill-down reads before it stops and says so. */
export const PAYMENT_ROW_CAP = 5000;

/**
 * Everything the Denials & Rejections page reads, behind one interface (FE-155): the provider console and the
 * Support console call different endpoints for the same data, and the page must not know which.
 *
 * ## Which endpoint each console uses
 *
 * | Data | Provider | Support |
 * |---|---|---|
 * | adjustment-remark report | `/provider/report/adjustment-remark` | the same — see below |
 * | transmission-status report | `/provider/report/transmission_status` | the same — see below |
 * | payments by adjustment code | `/provider/payment` | `/support/payment` |
 * | the claim a payment settles | `/provider/claim?filterPaymentId=` | `/support/claim?filterPaymentId=` |
 * | claim details, worker history | `/provider/claim/{id}/details`, `/worker/history` | `/support/claim/{id}/…` |
 *
 * **There is no Support report endpoint.** `/support/report/adjustment-remark`, `/adjustment`, `/remark` and
 * `/transmission_status` all answer 404 (measured on production 2026-10-06, against 401 for routes that exist).
 * Support reads the provider reports for the account it has switched to — both answer 200 to a Support token,
 * with that account's rows (production, 2026-10-06) — as it already reads other provider endpoints
 * (`listProviderBankingAccounts`, `getProviderPatientStatement`, `listProviderUsers`).
 *
 * `/support/payment` needs the payment-date window the drill-down always sends: without one, filtered by `EQ:45`
 * alone, it ran past the gateway's 60 s and answered 504; with the week it answers in about 2 s, the same rows as
 * `/provider/payment`.
 */
export interface IDenialsApi {
    /**
     * The adjustment-remark report — service-line adjustments grouped by adjustment group,
     * adjustment code, AND the distinct remark-code set on the line.
     *
     * This is what the reworked page reads instead of `fetchAdjustmentReport`, and it answers what
     * that one could not: the remark half of the code, and a usable amount.
     *
     * Three things measured on production 2026-09-30 that the request shape does not show:
     *
     * - it costs about **11.7 s** for a 7-day window (189 rows, 36 KB), against 1.6 s for the
     *   equivalent payment query, so it is a page load and not something to re-run on every filter
     *   change;
     * - `start` is inclusive and `end` is EXCLUSIVE — build the window with `denialsWeekWindow`
     *   rather than by hand, or a week silently becomes six days;
     * - it returns EVERY adjustment, contractual write-offs included, not only denials.
     */
    fetchAdjustmentRemarkReport(
        request: GetAdjustmentRemarkReportRequest,
    ): Promise<ProviderAdjustmentRemarkReportItem[] | undefined>;

    /**
     * The transmission-status report, filtered to rejections.
     *
     * An AGGREGATE — one row per distinct message, with a count and a charge — not a list of claims.
     * The per-claim source cannot back a worklist: measured on production 2026-09-30,
     * `/provider/status` ignores `filterCreated` completely, has no status-type filter, and costs
     * 2000 records / 3.7 MB / 36 s to surface about seventy rejections. See `rejection-rows.ts`.
     *
     * `REJECTED` is the platform's own definition and is broader than the X12 rejection categories:
     * it counted 136 over a week where an A3+A7 reading of the same window counted 34. We use it for
     * consistency with the worker rejection configuration page and say so on screen (FE-116).
     *
     * Start is inclusive, end is EXCLUSIVE, like every other report here.
     */
    fetchRejectionReport(
        start: string,
        end: string,
        type?: ClaimTransmissionStatusType,
    ): Promise<ProviderTransmissionStatusReportItem[] | undefined>;

    /**
     * Every payment in a window that carries a given adjustment reason code on any line.
     *
     * ## Not filtered to DENIED, and that is the whole point
     *
     * The first version asked for `filterStatus=EQ:DENIED`, on the reasoning that a denials table's
     * drill-down shows denied payments. It found **nothing** for a row with 198 adjustments. Measured
     * on production 2026-10-01:
     *
     *     OA:23  + status=DENIED   ->   0 payments
     *     OA:23  reason only       -> 100 payments, 100/100 carry 23 on a line,
     *                                 PROCESSED_AS_SECONDARY 26 · REVERSAL_OF_PREVIOUS_PAYMENT 74
     *     CO:B13 reason only       -> 100 payments, 100/100 carry B13, all PROCESSED_AS_PRIMARY
     *
     * A denial ADJUSTMENT is not a DENIED PAYMENT. A line can be denied while its payment as a whole
     * is processed, and the table counts adjustments by reason code across every payment status. So
     * the drill-down scopes by the code instead.
     *
     * ## Reason only, not reason + group
     *
     * Adding `filterAdjustmentGroup` returned the same set but took 8.0 s against 1.4 s. The group
     * still matters — PR:23 is not OA:23 — but `selectPaymentsForRow` already enforces it at the line
     * item, so the server only has to narrow and the client keeps it exact.
     *
     * `BT` on `filterPaymentDate` is read as inclusive at both ends, so the caller passes the window's
     * inclusive LAST day rather than the exclusive `end` the reports take.
     */
    fetchPaymentsForCode(
        reasonCode: string,
        firstDay: string,
        lastDay: string,
    ): Promise<{ payments: ProviderClaimPayment[]; truncated: boolean }>;

    /**
     * The claim a payment settles.
     *
     * Through the LIST endpoint, because a payment carries no claim id: its `professionalId` is the
     * professional-claim sub-record's id, for which every by-id claim endpoint answers 500 (measured on
     * production 2026-10-01). Undefined when the payment resolves to no claim — the Phase 0 batches
     * came back one or two short, so that case is real.
     */
    fetchClaimForPayment(paymentId: string): Promise<DenialsClaim | undefined>;

    /** Codings, professionals, payments and the rest. 127–622 KB and 1.4–5.7 s on the claims sampled. */
    fetchClaimDetails(claimId: string): Promise<IDenialsClaimDetails | undefined>;

    fetchClaimHistory(claimId: string): Promise<ClaimWorkflowHistoryItem[] | undefined>;
}

/**
 * Pages of 100 until a short page or the cap. The cap is a safety stop, SAID when hit: the caller shows that the
 * list stops short rather than presenting a cut-off set as the whole week (Copilot, PR #581). Rows, not
 * payments — the list repeats a payment once per matching line, so the cap reaches fewer distinct payments.
 */
const pagedPayments = async (
    page: (offset: number) => Promise<ProviderClaimPayment[] | undefined>,
): Promise<{ payments: ProviderClaimPayment[]; truncated: boolean }> => {
    const all: ProviderClaimPayment[] = [];
    for (let offset = 0; offset < PAYMENT_ROW_CAP; offset += 100) {
        const rows = (await page(offset)) || [];
        all.push(...rows);
        if (rows.length < 100) {
            return { payments: all, truncated: false };
        }
    }

    return { payments: all, truncated: true };
};

/** The reports: provider endpoints on BOTH consoles — there is no Support report endpoint (see the interface). */
const reports: Pick<IDenialsApi, 'fetchAdjustmentRemarkReport' | 'fetchRejectionReport'> = {
    fetchAdjustmentRemarkReport: request => CoreFetch<ReportApi>(ReportApi).getAdjustmentRemarkReport(request),
    fetchRejectionReport: (start, end, type = ClaimTransmissionStatusType.REJECTED) =>
        CoreFetch<ReportApi>(ReportApi).getTransmissionStatusReport({ start, end, transmissionStatusType: type }),
};

export const providerDenialsApi: IDenialsApi = {
    ...reports,
    fetchPaymentsForCode: (reasonCode, firstDay, lastDay) =>
        pagedPayments(offset =>
            CoreFetch<PaymentApi>(PaymentApi).listProviderClaimPayments({
                filterAdjustmentReason: [`EQ:${reasonCode}`],
                filterPaymentDate: [`BT:${firstDay}:${lastDay}`],
                limit: 100,
                offset,
            }),
        ),
    fetchClaimForPayment: async paymentId =>
        (await CoreFetch<ClaimApi>(ClaimApi).listProviderClaims({ filterPaymentId: [`EQ:${paymentId}`], limit: 5 }))[0],
    fetchClaimDetails: claimId => CoreFetch<ClaimApi>(ClaimApi).getProviderClaimDetails({ id: claimId }),
    fetchClaimHistory: claimId => CoreFetch<ClaimApi>(ClaimApi).getClaimWorkflowHistoryProvider({ id: claimId }),
};

/**
 * Support's own endpoints wherever Support has one. `SupportClaimPayment` is assignable to `ProviderClaimPayment`
 * as it stands; the claim and its details reach the page through `DenialsClaim` and `IDenialsClaimDetails`, which
 * both consoles' models meet.
 */
export const supportDenialsApi: IDenialsApi = {
    ...reports,
    fetchPaymentsForCode: (reasonCode, firstDay, lastDay) =>
        pagedPayments(offset =>
            CoreFetch<PaymentApi>(PaymentApi).listSupportClaimPayments({
                filterAdjustmentReason: [`EQ:${reasonCode}`],
                filterPaymentDate: [`BT:${firstDay}:${lastDay}`],
                limit: 100,
                offset,
            }),
        ),
    fetchClaimForPayment: async paymentId =>
        (await CoreFetch<ClaimApi>(ClaimApi).listSupportClaims({ filterPaymentId: [`EQ:${paymentId}`], limit: 5 }))[0],
    fetchClaimDetails: claimId => CoreFetch<ClaimApi>(ClaimApi).getSupportClaimDetails({ id: claimId }),
    // The Support-scoped history is the one WITHOUT a suffix; `…Provider` is the provider console's.
    fetchClaimHistory: claimId => CoreFetch<ClaimApi>(ClaimApi).getClaimWorkflowHistory({ id: claimId }),
};
